import axios from 'axios'

import { generateApiPath } from '@scouts/helpers'

import {
    TAX_OFFICE_AUTHENTICATION_ADDRESS_CONFIRMATION_ERROR_TYPE_CADASTRAL_REFERENCE_MISSING,
    TAX_OFFICE_AUTHENTICATION_ADDRESS_COULD_NOT_BE_SYNCHRONIZED,
} from '@/domain/tax-office'
import { TaxOfficeAddressConfirmationError, TaxOfficeAuthenticationError } from '@/helpers/requestErrors'

import { receiveSuccessAlert } from './alerts'
import { handleRequestError } from './request'

export const REQUEST_TAX_OFFICE_DATA = 'REQUEST_TAX_OFFICE_DATA'
export const RECEIVE_TAX_OFFICE_DATA = 'RECEIVE_TAX_OFFICE_DATA'
export const REQUEST_TAX_OFFICE_FISCAL_ADDRESS = 'REQUEST_TAX_OFFICE_FISCAL_ADDRESS'
export const RECEIVE_TAX_OFFICE_FISCAL_ADDRESS = 'RECEIVE_TAX_OFFICE_FISCAL_ADDRESS'
export const RECEIVE_TAX_OFFICE_FISCAL_ADDRESS_ERROR = 'RECEIVE_TAX_OFFICE_FISCAL_ADDRESS_ERROR'
export const REQUEST_TAX_OFFICE_REFERENCE_NUMBER = 'REQUEST_TAX_OFFICE_REFERENCE_NUMBER'
export const RECEIVE_TAX_OFFICE_REFERENCE_NUMBER = 'RECEIVE_TAX_OFFICE_REFERENCE_NUMBER'
export const REQUEST_TAX_OFFICE_CLAVE_PIN = 'REQUEST_TAX_OFFICE_CLAVE_PIN'
export const RECEIVE_TAX_OFFICE_CLAVE_PIN = 'RECEIVE_TAX_OFFICE_CLAVE_PIN'
export const DELETE_TAX_OFFICE_AUTHENTICATION_TOKEN = 'DELETE_TAX_OFFICE_AUTHENTICATION_TOKEN'
export const AUTHENTICATE_TAX_OFFICE = 'AUTHENTICATE_TAX_OFFICE'
export const REQUEST_TAX_OFFICE_ADDRESS_CONFIRMATION = 'REQUEST_TAX_OFFICE_ADDRESS_CONFIRMATION'
export const RECEIVE_TAX_OFFICE_ADDRESS_CONFIRMATION_SUCCESS = 'RECEIVE_TAX_OFFICE_ADDRESS_CONFIRMATION_SUCCESS'
export const RECEIVE_TAX_OFFICE_ADDRESS_CONFIRMATION_ERROR = 'RECEIVE_TAX_OFFICE_ADDRESS_CONFIRMATION_ERROR'

const receiveTaxOfficeFiscalAddress = (data = {}) => ({ type: RECEIVE_TAX_OFFICE_FISCAL_ADDRESS, data })

const receiveTaxOfficeFiscalAddressError = (error) => ({ type: RECEIVE_TAX_OFFICE_FISCAL_ADDRESS_ERROR, data: error })

export const requestClavePin = (data, query) => (dispatch) => {
    dispatch({ type: REQUEST_TAX_OFFICE_CLAVE_PIN })

    const url = generateApiPath('/taxoffice/requestpin', undefined, {
        queryParameters: {
            sendSms: query.sendSms,
        },
    })

    return axios
        .post(url, data)
        .then(() => dispatch({ type: RECEIVE_TAX_OFFICE_CLAVE_PIN }))
        .catch((error) => {
            const {
                data: {
                    message,
                    modelState: {
                        ErrorType: [errorType],
                        Message: modelStateMessage,
                    },
                },
            } = error

            dispatch(handleRequestError(error, { hideAlert: true }))

            return Promise.reject(new TaxOfficeAuthenticationError(modelStateMessage || message, errorType))
        })
}

export const authenticateInTaxOffice = (data) => (dispatch) => {
    dispatch({ type: AUTHENTICATE_TAX_OFFICE })

    return axios
        .post('/taxoffice/accesstoken', data)
        .then((response) => {
            const referenceNumber = response?.data
            dispatch({ type: RECEIVE_TAX_OFFICE_REFERENCE_NUMBER, data: referenceNumber })
            dispatch(receiveSuccessAlert('Autenticación correcta'))
        })
        .catch((error) => {
            const {
                data: {
                    message,
                    modelState: {
                        ErrorType: [errorType],
                        Message: modelStateMessage,
                    },
                },
            } = error

            dispatch(
                handleRequestError(error, {
                    customMessage: `No hemos podido identificarte; por favor, comprueba que los datos son correctos`,
                })
            )

            return Promise.reject(new TaxOfficeAuthenticationError(modelStateMessage || message, errorType))
        })
}

export const deleteTaxOfficeAuthenticationToken = (userId) => (dispatch) => {
    dispatch({ type: DELETE_TAX_OFFICE_AUTHENTICATION_TOKEN })

    return axios
        .delete(`/taxoffice/accesstoken?userId=${userId}`)
        .then(() => Promise.resolve())
        .catch(() => Promise.resolve())
}

const TaxOfficeAddressErrorMessage = {
    [TAX_OFFICE_AUTHENTICATION_ADDRESS_CONFIRMATION_ERROR_TYPE_CADASTRAL_REFERENCE_MISSING]:
        'No parece que hayas confirmado tu dirección fiscal en la Agencia Tributaria; por favor, accede al portal de la Agencia Tributaria y confírmala',
    [TAX_OFFICE_AUTHENTICATION_ADDRESS_COULD_NOT_BE_SYNCHRONIZED]:
        'No se pudo sincronizar el domicilio fiscal por no estar ratificado, se requiere la ratificación en la Web de la Agencia Tributaria',
}

const TaxOfficeAddressDefaultErrorMessage =
    'No hemos podido recuperar la información sobre el estado de confirmación de tu dirección fiscal; por favor, refresca la página'

export const requestAddressFromTaxOffice =
    ({ shouldDisplayErrorIfNotConfirmed = false } = {}) =>
    (dispatch, getState) => {
        dispatch({ type: REQUEST_TAX_OFFICE_FISCAL_ADDRESS, data: { fetchSuccessful: null } })

        const { id: userId } = getState().profile

        return axios
            .get(`/taxoffice/address?userId=${userId}`)
            .then((response) => {
                const { isConfirmed: isAddressConfirmed, address } = response?.data || {}

                dispatch(receiveTaxOfficeFiscalAddress({ address, isAddressConfirmed }))

                if (!isAddressConfirmed && shouldDisplayErrorIfNotConfirmed) {
                    dispatch(
                        handleRequestError(new Error(), {
                            customMessage: `No parece que hayas confirmado tu dirección fiscal en la Agencia Tributaria; por favor, accede al portal de la Agencia Tributaria y confírmala`,
                        })
                    )
                }

                return isAddressConfirmed
            })
            .catch((error) => {
                const {
                    data: { message, modelState: { ErrorType: [errorType] = [], Message: modelStateMessage } = {} },
                } = error

                if (shouldDisplayErrorIfNotConfirmed) {
                    dispatch(
                        handleRequestError(error, {
                            customMessage:
                                TaxOfficeAddressErrorMessage[errorType] ?? TaxOfficeAddressDefaultErrorMessage,
                        })
                    )
                }

                const customError = new TaxOfficeAddressConfirmationError(modelStateMessage || message, errorType)

                dispatch(receiveTaxOfficeFiscalAddressError(customError))

                return Promise.reject(customError)
            })
    }

export const confirmTaxOfficeAddress = (address) => (dispatch, getState) => {
    dispatch({ type: REQUEST_TAX_OFFICE_ADDRESS_CONFIRMATION, address })

    const { id: userId } = getState().profile

    return axios
        .post(`/taxoffice/address/confirmation?userId=${userId}`, address)
        .then(() => {
            dispatch({
                type: RECEIVE_TAX_OFFICE_ADDRESS_CONFIRMATION_SUCCESS,
            })
        })
        .catch((error) => {
            const {
                data: {
                    message,
                    modelState: {
                        ErrorType: [errorType],
                        Message: modelStateMessage,
                    },
                },
            } = error

            dispatch({
                type: RECEIVE_TAX_OFFICE_ADDRESS_CONFIRMATION_ERROR,
                data: { fetchSuccessful: false },
            })

            dispatch(
                handleRequestError(error, {
                    customMessage: `No hemos podido confirmar tu dirección. Por favor, refresca la página.`,
                })
            )

            return Promise.reject(new TaxOfficeAddressConfirmationError(modelStateMessage || message, errorType))
        })
}
